<template>
  <div>
    <b-card no-body class="users-card">
      <!-- Filter bar -->
      <div class="filter-bar p-3 border-bottom">
        <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem">
          <b-input-group size="sm" class="search-group">
            <b-input-group-prepend is-text>
              <i class="fas fa-search"></i>
            </b-input-group-prepend>
            <b-form-input v-model="searchText" placeholder="ค้นหาชื่อ / อีเมล..." />
          </b-input-group>

          <div
            v-if="centralRoleFacets.length"
            class="d-flex align-items-center flex-wrap"
            style="gap: 0.4rem"
          >
            <small class="text-muted">บทบาทคลินิก:</small>
            <span
              v-for="f in centralRoleFacets"
              :key="f.key"
              :class="[
                'role-chip',
                `role-${f.key.toLowerCase()}`,
                { 'role-chip--active': roleFilter.includes(f.key) },
                { 'role-chip--inactive': roleFilter.length && !roleFilter.includes(f.key) },
              ]"
              role="button"
              @click="toggleRoleFilter(f.key)"
            >
              <span class="role-chip__label">{{ f.label }}</span>
              <span class="role-chip__count">{{ f.count }}</span>
            </span>
            <b-button
              v-if="roleFilter.length"
              variant="link"
              size="sm"
              class="p-0 ml-1"
              @click="roleFilter = []"
            >
              ล้าง
            </b-button>
          </div>

          <div class="ml-auto d-flex align-items-center" style="gap: 0.5rem">
            <small v-if="!isLoading" class="text-muted">
              {{ filteredUsers.length }} / {{ users.length }} คน
            </small>
            <b-button
              v-if="canAdd"
              size="sm"
              variant="primary"
              data-testid="clinic-users-add-button"
              @click="openAdd"
            >
              <i class="fas fa-plus mr-1"></i> เพิ่มผู้ใช้
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="refresh">
              <b-spinner v-if="isLoading" small class="mr-1" />
              <i v-else class="fas fa-sync-alt mr-1"></i>
              รีเฟรช
            </b-button>
          </div>
        </div>
      </div>

      <b-table
        :items="filteredUsers"
        :fields="fields"
        :busy="isLoading"
        hover
        responsive
        empty-text="ไม่มีผู้ใช้"
        show-empty
        class="user-table mb-0"
        thead-class="user-table__head"
        tbody-tr-class="user-table__row"
      >
        <template #table-busy>
          <div class="text-center py-4">
            <b-spinner class="align-middle" />
            <span class="ml-2">กำลังโหลด...</span>
          </div>
        </template>

        <template #cell(index)="row">
          <span class="row-index">{{ row.index + 1 }}</span>
        </template>

        <template #cell(name)="row">
          <strong class="user-name">
            {{ row.item.firstNameTh }} {{ row.item.lastNameTh }}
          </strong>
        </template>

        <template #cell(email)="row">
          <span v-if="row.item.email" class="user-email">{{ row.item.email }}</span>
          <span v-else class="text-muted small">—</span>
        </template>

        <template #cell(branchCount)="row">
          <span class="branch-pill">
            <i class="fas fa-store-alt mr-1"></i>{{ row.item.branchCount || 0 }} สาขา
          </span>
        </template>

        <template #cell(centralRoles)="row">
          <div
            v-if="row.item.centralRoles && row.item.centralRoles.length"
            class="d-flex flex-wrap"
            style="gap: 0.35rem"
          >
            <span
              v-for="rk in row.item.centralRoles"
              :key="rk"
              :class="['role-chip', `role-${rk.toLowerCase()}`]"
            >
              <span class="role-chip__label">{{ roleLabel(rk) }}</span>
            </span>
          </div>
          <span v-else class="text-muted small">—</span>
        </template>

        <template #cell(actions)="row">
          <b-button
            size="sm"
            variant="link"
            class="row-actions__toggle p-1"
            :title="`แก้ไข ${row.item.firstNameTh || ''}`"
            @click="openUser(row.item)"
          >
            <i class="fas fa-ellipsis-h"></i>
            <span class="sr-only">แก้ไข</span>
          </b-button>
        </template>
      </b-table>
    </b-card>

    <UserDetailModal ref="UserDetailModal" @updated="refresh" />
    <AddUserModal ref="AddUserModal" @added="refresh" />
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import { ROLE_LABELS_TH } from "@/constants/roles";
import UserDetailModal from "@/components/user-mgmt/UserDetailModal.vue";
import AddUserModal from "@/components/user-mgmt/AddUserModal.vue";

export default {
  name: "ClinicUsers",
  components: {
    UserDetailModal,
    AddUserModal,
  },
  data() {
    return {
      users: [],
      branches: [],
      isLoading: false,
      searchText: "",
      roleFilter: [],
      fields: [
        {
          key: "index",
          label: "#",
          thStyle: { width: "48px" },
          tdClass: "align-middle",
        },
        { key: "name", label: "ชื่อ", sortable: true, tdClass: "align-middle" },
        {
          key: "email",
          label: "อีเมล",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "branchCount",
          label: "จำนวนสาขา",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "centralRoles",
          label: "บทบาทระดับคลินิก",
          tdClass: "align-middle",
        },
        {
          key: "actions",
          label: "",
          thStyle: { width: "52px" },
          tdClass: "align-middle text-right",
        },
      ],
    };
  },
  computed: {
    ...mapGetters({ assignableRoles: "moduleUser/assignableRoles" }),
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    // Show the "+ เพิ่มผู้ใช้" button only if the actor can assign at least one
    // role in some scope. The server is the real gate (buildAssignContext).
    canAdd() {
      return (
        this.assignableRoles("clinic").length > 0 ||
        this.assignableRoles("branch").length > 0
      );
    },
    // Distinct central roles present in the data, with counts, for filter chips.
    centralRoleFacets() {
      const counts = {};
      this.users.forEach(u => {
        (u.centralRoles || []).forEach(rk => {
          counts[rk] = (counts[rk] || 0) + 1;
        });
      });
      return Object.keys(counts).map(key => ({
        key,
        label: this.roleLabel(key),
        count: counts[key],
      }));
    },
    filteredUsers() {
      const q = this.searchText.trim().toLowerCase();
      return this.users.filter(u => {
        const name = `${u.firstNameTh || ""} ${u.lastNameTh || ""}`.toLowerCase();
        const email = String(u.email || "").toLowerCase();
        const matchesSearch = !q || name.includes(q) || email.includes(q);
        const matchesRole =
          this.roleFilter.length === 0 ||
          (u.centralRoles || []).some(rk => this.roleFilter.includes(rk));
        return matchesSearch && matchesRole;
      });
    },
  },
  async created() {
    await this.ensurePermissionContext();
    await Promise.all([this.refresh(), this.loadBranches()]);
  },
  methods: {
    roleLabel(rk) {
      return ROLE_LABELS_TH[rk] || rk;
    },
    toggleRoleFilter(key) {
      const i = this.roleFilter.indexOf(key);
      if (i === -1) this.roleFilter.push(key);
      else this.roleFilter.splice(i, 1);
    },
    async ensurePermissionContext() {
      // ensurePermissionContextFor handles its own cache check by clinicURL —
      // re-loads only when the active context belongs to a different clinic.
      try {
        await this.$store.dispatch("moduleUser/ensurePermissionContextFor", {
          clinicURL: this.clinicUrl,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("loadPermissionContext failed", err);
      }
    },
    async refresh() {
      this.isLoading = true;
      try {
        const res = await axios.get(`/api/userMgmt/${this.clinicUrl}/users`);
        const payload = res.data || {};
        this.users = Array.isArray(payload.users) ? payload.users : [];
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load clinic users", err);
        this.users = [];
      } finally {
        this.isLoading = false;
      }
    },
    openUser(item) {
      this.$refs.UserDetailModal.show({
        clinicUrl: this.clinicUrl,
        uid: item.uid,
      });
    },
    // Load the clinic's branches (for the AddUserModal lookup + branch picker).
    async loadBranches() {
      try {
        const res = await this.$store.dispatch("moduleUser/fetchClinicList");
        const list = (res && res.data) || [];
        // Route param `clinicUrl` matches the clinic's `code` (see AuditLog.vue).
        const clinic = list.find(c => c.code === this.clinicUrl);
        this.branches = (clinic && clinic.branches) || [];
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("loadBranches failed", err);
        this.branches = [];
      }
    },
    openAdd() {
      this.$refs.AddUserModal.show({
        clinicUrl: this.clinicUrl,
        branches: this.branches,
      });
    },
  },
};
</script>

<style scoped>
.users-card {
  border-radius: 10px;
  overflow: hidden;
  border-color: #e6eaf0;
}

.filter-bar {
  background: #fbfcfd;
}

.search-group {
  max-width: 280px;
}

/* ---------- Index ---------- */
.row-index {
  display: inline-block;
  min-width: 28px;
  color: #8a94a6;
  font-variant-numeric: tabular-nums;
  font-size: 0.82rem;
  font-weight: 500;
}

/* ---------- Name ---------- */
.user-name {
  color: #1f2937;
  font-weight: 600;
}

.user-email {
  color: #475569;
  font-size: 0.85rem;
  word-break: break-all;
}

/* ---------- Branch pill ---------- */
.branch-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: #f1f3f5;
  color: #495057;
  font-size: 0.78rem;
  font-weight: 500;
}

/* ---------- Role chips ---------- */
.role-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.22rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  border: 1px solid transparent;
  white-space: nowrap;
  transition: filter 0.12s ease, opacity 0.12s ease;
}

.role-chip__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.1rem;
  padding: 0.05rem 0.3rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.55);
  font-size: 0.7rem;
  font-weight: 700;
}

/* Filter chip states */
.role-chip[role="button"] {
  cursor: pointer;
}
.role-chip[role="button"]:hover {
  filter: brightness(0.96);
}
.role-chip--inactive {
  opacity: 0.45;
}
.role-chip--active {
  box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.08);
}

/* Per-role palette */
.role-c_level {
  background: #ffe4e6;
  color: #9f1239;
  border-color: #fecdd3;
}
.role-rdm {
  background: #fef3c7;
  color: #92400e;
  border-color: #fde68a;
}
.role-accountant {
  background: #dbeafe;
  color: #1e40af;
  border-color: #bfdbfe;
}
.role-procurement {
  background: #d1fae5;
  color: #065f46;
  border-color: #a7f3d0;
}

/* ---------- Table ---------- */
.user-table >>> .user-table__head th {
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: 0.78rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border-top: none;
  border-bottom: 1px solid #e6eaf0;
}

.user-table >>> tbody td {
  border-top: 1px solid #f1f3f5;
  padding-top: 0.65rem;
  padding-bottom: 0.65rem;
  font-size: 0.88rem;
}

.user-table >>> tbody tr {
  transition: background-color 0.12s ease;
}
.user-table >>> tbody tr:hover {
  background-color: #fafbfc;
}

/* ---------- Row actions (...) ---------- */
.row-actions__toggle {
  color: #6b7280;
  border-radius: 6px;
  line-height: 1;
}
.row-actions__toggle:hover,
.row-actions__toggle:focus {
  background: #eef2f7;
  color: #1f2937;
  text-decoration: none;
  box-shadow: none;
}
</style>
